import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { UserDialogComponent, UserDialogData } from '@/components/user-dialog/user-dialog.component';
import { DateTimeFormatPipe } from '@/extensions/dateTimePipe';
import { UserHelper } from '@/helpers/user.helper';
import { Paging } from '@/models/Paging';
import { AlertService } from '@/services/alert.service';
import { AuthenticationService } from '@/services/authentication.service';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AccountType, AdminService, SortDirection, User, UserRole, UsersService } from '@eemo/api-client';
import { Subscription, catchError, map, merge, of, startWith, switchMap } from 'rxjs';
import { BooleanFormatPipe } from '../../../extensions/booleanPipe';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    NgFor,
    MatSlideToggleModule,
    MatButtonModule,
    MatDividerModule,
    NgIf,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatPaginatorModule,
    DateTimeFormatPipe,
    BooleanFormatPipe,
    TooltipDirective
  ]
})
export class AdminUsersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<User>;
  data: User[];
  displayFullColumns = false;
  tableClass = 'light';
  lightColumns = [
    'name',
    'firstName',
    'email',
    'role',
    'accountType',
    'active',
    'lastConnectionDate',
    'emailValidated',
    'actions'
  ];
  fullColumns = [
    'name',
    'firstName',
    'email',
    'role',
    'accountType',
    'phoneNumber',
    'company',
    'active',
    'lastConnectionDate',
    'emailValidated',
    'dateCreate',
    'dateUpdate',
    'uid',
    'externalId',
    'actions'
  ];
  displayedColumns = [];
  isLoadingResults = true;

  search: string;
  filterRole: UserRole;

  availableRoles: UserRole[];
  rolesDisplay: { [key: string]: string } = {};

  sub = new Subscription();
  sortSub = new Subscription();
  deleteSub = new Subscription();
  activateSub = new Subscription();
  deActivateSub = new Subscription();
  sendMailSub = new Subscription();
  upgradeSub = new Subscription();
  downgradeSub = new Subscription();
  resetSub = new Subscription();
  assumeSub = new Subscription();
  assumeSessionSub = new Subscription();

  paging = new Paging();

  constructor(
    private userService: UsersService,
    private adminService: AdminService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private changeDetectorRefs: ChangeDetectorRef,
    public userHelper: UserHelper,
    private authenticationService: AuthenticationService
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.availableRoles = Object.keys(UserRole) as UserRole[];
    this.availableRoles.forEach((r) => (this.rolesDisplay[r] = this.userHelper.getRoleName(r)));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.sortSub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.activateSub.unsubscribe();
    this.deActivateSub.unsubscribe();
    this.sendMailSub.unsubscribe();
    this.upgradeSub.unsubscribe();
    this.downgradeSub.unsubscribe();
    this.resetSub.unsubscribe();
    this.assumeSub.unsubscribe();
    this.assumeSessionSub.unsubscribe();
  }

  loadData() {
    this.sub = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          const sortDirection = this.sort.direction
            ? this.sort.direction == 'desc'
              ? SortDirection.DESC
              : SortDirection.ASC
            : null;
          const sortField = this.sort.active as 'name' | 'email' | 'lastConnectionDate' | 'dateCreate' | 'dateUpdate';
          return this.userService.getUsers(
            this.filterRole ? [this.filterRole] : null,
            this.search || null,
            sortField,
            sortDirection,
            this.paginator.pageIndex * this.paginator.pageSize,
            this.paginator.pageSize
          );
        }),
        map((dataPage) => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.paging.length = dataPage.paging.totalItems;
          return dataPage.list;
        }),
        catchError((error) => {
          this.isLoadingResults = false;
          return of([]);
        })
      )
      .subscribe((data) => {
        const displayedData = [];
        data.forEach((i) => {
          const row = {
            user: i,
            uid: i.uid,
            name: i.name,
            firstName: i.firstName,
            email: i.email,
            role: i.role,
            accountType: i.accountType,
            photoNummber: i.phoneNumber,
            company: i.companies?.length > 0 ? i.companies.map((c) => c.company.name).join(', ') : '',
            isManagedExternally: this.userHelper.isManagedExternally(i),
            isAccountTypePremium: this.userHelper.isAccountTypePremium(i.accountType),
            isRolePremiumOnly: this.userHelper.isRolePremiumOnly(i.role),
            isAdmin: UserRole.ADMIN === i.role
          };
          displayedData.push(row);
        });
        this.dataSource = new MatTableDataSource(displayedData);
      });
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sortSub = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.loadData();
  }

  applyFilter(filterValue: string) {
    if (!filterValue || filterValue.length > 1) {
      this.search = filterValue.trim().toLowerCase();
      this.paginator.pageIndex = 0;
      this.loadData();
    }
  }

  updateDataFromFilters() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
      this.tableClass = 'light';
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
      this.tableClass = 'full';
    }
    this.changeDetectorRefs.detectChanges();
  }

  add() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(UserDialogComponent, {
        // width: '20rem'
        data: new UserDialogData()
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  edit(userToEdit: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(UserDialogComponent, {
        // width: '20rem'
        data: { user: userToEdit, edition: true }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDelete(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression de l\'utilisateur "' + UserHelper.userDisplayName(user) + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(user);
        }
      });
    }
  }

  doDelete(user: User) {
    this.deleteSub = this.userService.deleteUser(user.uid).subscribe((result) => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }

  confirmSendValidationMail(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            "Confirmez-vous l'envoi du mail de validation à l'utilisateur \"" + UserHelper.userDisplayName(user) + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doSendValidationMail(user);
        }
      });
    }
  }

  doSendValidationMail(user: User) {
    this.sendMailSub = this.userService.sendValidateUserEmail(user.uid).subscribe((result) => {
      this.alertService.success('Email de validation envoyé');
      this.loadData();
    });
  }

  confirmActivateUser(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Confirmez-vous l'activation de l'utilisateur \"" + UserHelper.userDisplayName(user) + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doActivateUser(user);
        }
      });
    }
  }

  doActivateUser(user: User) {
    this.activateSub = this.userService.manageUserActivation(user.uid, true).subscribe((result) => {
      this.alertService.success('Utilisateur activé');
      this.loadData();
    });
  }

  confirmDeActivateUser(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la désactivation de l\'utilisateur "' + UserHelper.userDisplayName(user) + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeActivateUser(user);
        }
      });
    }
  }

  doDeActivateUser(user: User) {
    this.deActivateSub = this.userService.manageUserActivation(user.uid, false).subscribe((result) => {
      this.alertService.success('Utilisateur désactivé');
      this.loadData();
    });
  }

  confirmUpgradeUserToPremium(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous le passage de l\'utilisateur "' + UserHelper.userDisplayName(user) + '" en mode PREMIUM ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doUpgradeUserToPremium(user);
        }
      });
    }
  }

  doUpgradeUserToPremium(user: User) {
    this.upgradeSub = this.userService.manageUserAccountType(user.uid, AccountType.PREMIUM).subscribe((result) => {
      this.alertService.success('Utilisateur passé en mode PREMIUM');
      this.loadData();
    });
  }

  confirmDowngradeUserToFree(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous le rétrogradage de l\'utilisateur "' + UserHelper.userDisplayName(user) + '" en mode FREE ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDowngradeUserToFree(user);
        }
      });
    }
  }

  doDowngradeUserToFree(user: User) {
    this.downgradeSub = this.userService.manageUserAccountType(user.uid, AccountType.FREE).subscribe((result) => {
      this.alertService.success('Utilisateur rétrogradé en mode FREE');
      this.loadData();
    });
  }

  confirmResetUserPassword(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            'Confirmez-vous la réinitialisation du mot de passe de l\'utilisateur "' +
            user.name +
            '" ? Un email va lui être envoyé avec un nouveau mot de passe.'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doResetUserPassword(user);
        }
      });
    }
  }

  doResetUserPassword(user: User) {
    this.resetSub = this.adminService.adminLostUserPassword(user.email).subscribe((result) => {
      this.alertService.success("Mot de passe réinitialisé (email envoyé à l'utilisateur)");
      this.loadData();
    });
  }

  confirmAssumeUser(user: User) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Confirmez-vous l'ouverture d'une session avec \"" + UserHelper.userDisplayName(user) + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doAssumeUser(user);
        }
      });
    }
  }

  doAssumeUser(user: User) {
    this.assumeSub = this.adminService.adminAssumeUser(user.uid).subscribe((result) => {
      this.assumeSessionSub = this.authenticationService.assumeUser().subscribe(() => {
        location.reload();
      });
    });
  }
}
